import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import type {
  AuthError,
  Session,
} from '@supabase/supabase-js';

import { supabase } from '../../services/supabaseClient';
import type { ForfeoPermission } from '../../lib/forfeoCanonicalIdentity';

interface ForfeoProtectedRouteProps {
  children: ReactNode;
  requiredPermission: ForfeoPermission;
  loginPath?: string;
}

interface CurrentIdentityRow {
  user_id?: unknown;
  canonical_role?: unknown;
  onboarding_status?: unknown;
  identity_review_required?: unknown;
}

interface AccountLifecycleRow {
  account_status?: unknown;
}

type GuardErrorStatus = 'rpc_error' | 'network_error';

type GuardState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | {
      status: 'forbidden';
      reason: string;
      identity: CurrentIdentityRow | null;
    }
  | {
      status: GuardErrorStatus;
      message: string;
    }
  | { status: 'allowed' };

const NETWORK_ERROR_PATTERN =
  /failed to fetch|network|load failed|timed?\s*out|connection|offline|econn|dns/i;

const UNAUTHENTICATED_ERROR_PATTERN =
  /auth session missing|invalid jwt|jwt expired|token.*expired|refresh token.*not found|user not found/i;

function errorMessage(error: unknown): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'Une erreur inconnue est survenue.';
}

function errorStatus(error: unknown): GuardErrorStatus {
  return NETWORK_ERROR_PATTERN.test(errorMessage(error))
    ? 'network_error'
    : 'rpc_error';
}

function isUnauthenticatedAuthError(
  error: AuthError | null,
): boolean {
  if (!error) {
    return false;
  }

  return (
    error.status === 401 ||
    UNAUTHENTICATED_ERROR_PATTERN.test(error.message)
  );
}

function currentIdentityRow(
  value: unknown,
): CurrentIdentityRow | null {
  const candidate = Array.isArray(value)
    ? value[0]
    : value;

  if (
    typeof candidate !== 'object' ||
    candidate === null
  ) {
    return null;
  }

  return candidate as CurrentIdentityRow;
}

function currentLifecycleRow(
  value: unknown,
): AccountLifecycleRow | null {
  const candidate = Array.isArray(value)
    ? value[0]
    : value;

  if (
    typeof candidate !== 'object' ||
    candidate === null
  ) {
    return null;
  }

  return candidate as AccountLifecycleRow;
}

function forbiddenReason(
  identity: CurrentIdentityRow | null,
): string {
  if (!identity) {
    return 'identity_missing';
  }

  if (identity.identity_review_required === true) {
    return 'identity_review_required';
  }

  return 'permission_denied';
}

interface GuardMessageProps {
  title: string;
  message: string;
  onRetry?: () => void;
  onBack?: () => void;
  backLabel?: string;
}

function GuardMessage({
  title,
  message,
  onRetry,
  onBack,
  backLabel = 'Retour',
}: GuardMessageProps) {
  return (
    <div
      className="min-h-[45vh] flex items-center justify-center px-4"
      role="alert"
      aria-live="assertive"
    >
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
        <h1 className="text-xl font-bold text-slate-900">
          {title}
        </h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          {message}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
            >
              Réessayer
            </button>
          )}
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
            >
              {backLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ForfeoProtectedRoute({
  children,
  requiredPermission,
  loginPath = '/login',
}: ForfeoProtectedRouteProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const evaluationId = useRef(0);
  const authorizedUserId = useRef<string | null>(null);
  // LOT0F7B_BACKGROUND_AUTH_REVALIDATION_V1

  const [retryToken, setRetryToken] = useState(0);
  const [state, setState] = useState<GuardState>({
    status: 'loading',
  });

  const evaluateSession = useCallback(async (
    session: Session | null,
    options: {
      background?: boolean;
    } = {},
  ) => {
    const background =
      options.background === true;

    const currentEvaluationId = ++evaluationId.current;

    const commit = (nextState: GuardState) => {
      if (currentEvaluationId === evaluationId.current) {
        setState(nextState);
      }
    };

    const commitTransientFailure = (
      nextState: GuardState,
    ) => {
      if (!background) {
        commit(nextState);
      }
    };

    if (!session?.user?.id) {
      authorizedUserId.current = null;
      commit({ status: 'unauthenticated' });
      return;
    }

    if (!background) {
      commit({ status: 'loading' });
    }

    try {
      // getUser valide le JWT côté Supabase. L'ID de session reste la
      // seule identité utilisée par le garde; aucun UUID fourni par le
      // navigateur n'est transmis aux RPC d'autorisation.
      const {
        data: userData,
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        if (isUnauthenticatedAuthError(userError)) {
          authorizedUserId.current = null;
          commit({ status: 'unauthenticated' });
          return;
        }

        commitTransientFailure({
          status: errorStatus(userError),
          message: errorMessage(userError),
        });
        return;
      }

      if (
        !userData.user?.id ||
        userData.user.id !== session.user.id
      ) {
        authorizedUserId.current = null;
        commit({
          status: 'rpc_error',
          message:
            'La session restaurée ne correspond pas à l’utilisateur authentifié.',
        });
        return;
      }

      const {
        data: permissionGranted,
        error: permissionError,
      } = await supabase.rpc(
        'forfeo_has_permission',
        { p_permission: requiredPermission },
      );

      if (permissionError) {
        commitTransientFailure({
          status: errorStatus(permissionError),
          message: errorMessage(permissionError),
        });
        return;
      }

      if (permissionGranted === true) {
        authorizedUserId.current =
          session.user.id;
        commit({ status: 'allowed' });
        return;
      }

      const {
        data: lifecycleData,
        error: lifecycleError,
      } = await supabase
        .rpc('forfeo_account_lifecycle_state')
        .maybeSingle();

      if (!lifecycleError) {
        const lifecycle =
          currentLifecycleRow(lifecycleData);

        const accountStatus =
          String(
            lifecycle?.account_status ?? '',
          );

        if (
          accountStatus === 'deactivated' ||
          accountStatus === 'deletion_requested'
        ) {
          authorizedUserId.current = null;
          commit({
            status: 'forbidden',
            reason: `account_${accountStatus}`,
            identity: null,
          });
          return;
        }
      }

      const {
        data: identityData,
        error: identityError,
      } = await supabase.rpc('forfeo_current_identity');

      if (identityError) {
        commitTransientFailure({
          status: errorStatus(identityError),
          message: errorMessage(identityError),
        });
        return;
      }

      const identity = currentIdentityRow(identityData);

      authorizedUserId.current = null;
      commit({
        status: 'forbidden',
        reason: forbiddenReason(identity),
        identity,
      });
    } catch (error) {
      commitTransientFailure({
        status: errorStatus(error),
        message: errorMessage(error),
      });
    }
  }, [requiredPermission]);

  useEffect(() => {
    let active = true;
    evaluationId.current += 1;
    setState({ status: 'loading' });

    const run = (
      session: Session | null,
      options: {
        background?: boolean;
      } = {},
    ) => {
      if (active) {
        void evaluateSession(
          session,
          options,
        );
      }
    };

    // Le listener est installé avant getSession afin de ne pas perdre
    // INITIAL_SESSION ou un changement d'état concurrent pendant la
    // restauration de la session persistée.
    const {
      data: authSubscription,
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) {
        return;
      }

      if (event === 'SIGNED_OUT') {
        evaluationId.current += 1;
        authorizedUserId.current = null;
        setState({ status: 'unauthenticated' });
        return;
      }

      const sameAuthorizedUser =
        Boolean(
          session?.user?.id
          && authorizedUserId.current
            === session.user.id,
        );

      if (
        event === 'TOKEN_REFRESHED'
        || event === 'USER_UPDATED'
        || (
          event === 'SIGNED_IN'
          && sameAuthorizedUser
        )
      ) {
        run(
          session,
          {
            background: true,
          },
        );
        return;
      }

      if (
        event === 'INITIAL_SESSION'
        || event === 'SIGNED_IN'
      ) {
        run(
          session,
          {
            background: false,
          },
        );
      }
    });

    void supabase.auth.getSession().then((result) => {
      if (!active) {
        return;
      }

      if (result.error) {
        if (isUnauthenticatedAuthError(result.error)) {
          evaluationId.current += 1;
          setState({ status: 'unauthenticated' });
          return;
        }

        evaluationId.current += 1;
        setState({
          status: errorStatus(result.error),
          message: errorMessage(result.error),
        });
        return;
      }

      run(
        result.data.session,
        {
          background: false,
        },
      );
    }).catch((error: unknown) => {
      if (!active) {
        return;
      }

      evaluationId.current += 1;
      setState({
        status: errorStatus(error),
        message: errorMessage(error),
      });
    });

    return () => {
      active = false;
      evaluationId.current += 1;
      authSubscription.subscription.unsubscribe();
    };
  }, [
    evaluateSession,
    location.pathname,
    location.search,
    retryToken,
  ]);

  const retry = () => {
    setRetryToken((value) => value + 1);
  };

  const returnToSafePage = () => {
    navigate('/', { replace: true });
  };

  if (state.status === 'loading') {
    return (
      <div
        className="min-h-[45vh] flex items-center justify-center"
        role="status"
        aria-live="polite"
        data-guard-state="loading"
      >
        <div className="text-center">
          <div
            className="mx-auto h-9 w-9 animate-spin rounded-full border-4 border-slate-200 border-t-slate-800"
            aria-hidden="true"
          />
          <p className="mt-3 text-sm font-semibold text-slate-600">
            Restauration et vérification sécurisée de votre session…
          </p>
        </div>
      </div>
    );
  }

  if (state.status === 'unauthenticated') {
    const redirect = encodeURIComponent(
      location.pathname + location.search,
    );

    return (
      <Navigate
        to={`${loginPath}?redirect=${redirect}`}
        replace
        state={{ from: location }}
      />
    );
  }

  if (state.status === 'forbidden') {
    const accountInactive =
      state.reason === 'account_deactivated' ||
      state.reason === 'account_deletion_requested';

    const message = accountInactive
      ? 'Votre compte est désactivé. Vous pouvez le réactiver depuis la gestion du compte.'
      : state.reason === 'identity_review_required'
        ? 'Votre identité doit être révisée avant que cet espace puisse être ouvert.'
        : 'Votre session est valide, mais votre profil ne possède pas la permission requise pour cette page.';

    return (
      <div data-guard-state="forbidden">
        <GuardMessage
          title={accountInactive ? 'Compte désactivé' : 'Accès refusé'}
          message={message}
          onRetry={retry}
          onBack={
            accountInactive
              ? () => navigate('/gestion-compte')
              : returnToSafePage
          }
          backLabel={
            accountInactive
              ? 'Gérer mon compte'
              : 'Retour'
          }
        />
      </div>
    );
  }

  if (state.status === 'network_error') {
    return (
      <div data-guard-state="network_error">
        <GuardMessage
          title="Connexion temporairement indisponible"
          message="La vérification de votre accès n’a pas pu joindre le service d’identité. Votre session n’a pas été considérée comme déconnectée."
          onRetry={retry}
          onBack={returnToSafePage}
        />
      </div>
    );
  }

  if (state.status === 'rpc_error') {
    return (
      <div data-guard-state="rpc_error">
        <GuardMessage
          title="Vérification d’accès impossible"
          message="Le service d’autorisation a retourné une erreur. Aucun accès n’a été accordé et vous n’avez pas été renvoyé silencieusement à la connexion."
          onRetry={retry}
          onBack={returnToSafePage}
        />
      </div>
    );
  }

  return <>{children}</>;
}
